import { useEffect, useMemo, useState } from 'react';
import { useClientUpdateApi } from '@hero/api-hooks';
import type { Client, ImageWithUpload, LegalEntity } from '@hero/schema';
import { fileToBase64 } from '@hero/web-utils';

export type LegalDraft = Partial<{
  registrationId: string;
  registrationName: string;
  taxId: string;
  licenseId: string;
  fullAddress: string;
  addressCity: string;
  addressCountry: LegalEntity['addressCountry'];

  /**
   * Whether the client tracks a transport license (carrier / broker
   * license). Default `false` — most CRM clients are end-customers
   * without a transport license, so the form keeps that section
   * collapsed by default.
   */
  hasLicense: boolean;

  /**
   * Per-image draft state. Stored as `ImageWithUpload` so the value
   * can be either:
   *   - the existing persisted image `{ id, url }` (no change),
   *   - a fresh upload `{ upload: 'data:image/…;base64,…' }`, or
   *   - `undefined` to indicate the user cleared the slot.
   *
   * `useEditClientLegalManager.setImage` converts a `File` from the
   * `FileUploader` into the `{ upload }` form via `fileToBase64`.
   */
  registrationImage: ImageWithUpload | undefined;
  taxImage: ImageWithUpload | undefined;
  licenseImage: ImageWithUpload | undefined;
}>;

const SCALAR_FIELDS: ReadonlyArray<keyof LegalDraft> = [
  'registrationId',
  'registrationName',
  'taxId',
  'licenseId',
  'fullAddress',
  'addressCity',
];

export type LegalImageField =
  | 'registrationImage'
  | 'taxImage'
  | 'licenseImage';

const IMAGE_FIELDS: ReadonlyArray<LegalImageField> = [
  'registrationImage',
  'taxImage',
  'licenseImage',
];

/**
 * Whether any license-related field is set on the persisted Client.
 * Used to seed the `hasLicense` toggle on open so editors of an
 * already-licensed client land on the expanded form, while clients
 * without a license see the toggle off and the heavy upload UI
 * stays collapsed.
 */
function deriveHasLicense(client?: Client): boolean {
  const legal = client?.legal;
  if (!legal) return false;
  return Boolean(
    legal.licenseId ||
      legal.licenseImage ||
      legal.licenseType ||
      legal.licenseExpiryDate,
  );
}

function clientToDraft(client?: Client): LegalDraft {
  const legal = client?.legal;
  return {
    registrationId: legal?.registrationId ?? '',
    registrationName: legal?.registrationName ?? '',
    taxId: legal?.taxId ?? '',
    licenseId: legal?.licenseId ?? '',
    fullAddress: legal?.fullAddress ?? '',
    addressCity: legal?.addressCity ?? '',
    addressCountry: legal?.addressCountry,
    hasLicense: deriveHasLicense(client),
    registrationImage: legal?.registrationImage,
    taxImage: legal?.taxImage,
    licenseImage: legal?.licenseImage,
  };
}

/**
 * Manager hook for the per-section "Edit legal" modal. Mirrors the
 * `Client.legal` sub-doc into local form state; on save merges the
 * draft with the existing legal sub-doc and PATCHes via
 * `useClientUpdateApi`.
 *
 * Now also owns:
 *   - CR / tax / license image uploads — exposes `setImageFile(field,
 *     file)` so the modal can wire `FileUploader` directly without
 *     learning about `fileToBase64` or `imageWithUploadSchema` shape.
 *   - `hasLicense` toggle — when off, all license-related fields
 *     (id, image, type, expiry) are cleared from the patch payload
 *     so the doc stays clean rather than carrying tombstoned bits
 *     of a license the user explicitly opted out of.
 */
export function useEditClientLegalManager(client: Client | undefined) {
  const updateClient = useClientUpdateApi();
  const [draft, setDraft] = useState<LegalDraft>(() => clientToDraft(client));

  // Local `File[]` state per uploader so the FileUploader renders the
  // freshly-picked file (filename, size) before we've had a chance to
  // convert it to base64 and stamp it into the draft.
  const [registrationFiles, setRegistrationFiles] = useState<File[]>([]);
  const [taxFiles, setTaxFiles] = useState<File[]>([]);
  const [licenseFiles, setLicenseFiles] = useState<File[]>([]);

  useEffect(() => {
    setDraft(clientToDraft(client));
    setRegistrationFiles([]);
    setTaxFiles([]);
    setLicenseFiles([]);
  }, [client?._id, client?.legal]);

  const setField = <K extends keyof LegalDraft>(field: K, value: LegalDraft[K]) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Wire-friendly image setter for `FileUploader.onChange`. Converts
   * the first picked file to a base64 data URL and stamps it into the
   * draft as `{ upload }`. Empty input clears the field. Keeps a
   * parallel `File[]` state so the uploader renders the picked file
   * preview without needing the persisted `url` to come back from the
   * server first.
   */
  const setImageFile = async (field: LegalImageField, files: File[]) => {
    if (field === 'registrationImage') setRegistrationFiles(files);
    else if (field === 'taxImage') setTaxFiles(files);
    else setLicenseFiles(files);

    const file = files[0];
    if (!file) {
      setField(field, undefined);
      return;
    }
    const upload = await fileToBase64(file);
    setField(field, { upload } as ImageWithUpload);
  };

  const reset = () => {
    setDraft(clientToDraft(client));
    setRegistrationFiles([]);
    setTaxFiles([]);
    setLicenseFiles([]);
    // Clear the mutation's `isSuccess` so re-opening the modal after a
    // successful save doesn't immediately auto-close (ModalForm watches
    // `isSuccess` to drive its 300ms close animation).
    updateClient.reset();
  };

  const onSave = async () => {
    if (!client) return;
    // Merge the draft on top of the existing sub-doc so non-edited fields
    // (e.g. registrationExpiryDate, licenseType) are preserved across the
    // partial replacement.
    const next: LegalEntity = {
      ...(client.legal ?? {}),
    };
    for (const field of SCALAR_FIELDS) {
      const raw = draft[field] as string | undefined;
      const value = (raw ?? '').trim();
      // `undefined` lets the schema clear it via the `optionalSparse` preprocessor.
      (next as Record<string, unknown>)[field] = value || undefined;
    }
    next.addressCountry = draft.addressCountry;

    // Image fields — only stamp `{ upload }` when the user picked a
    // new file; otherwise pass the existing `{ id, url }` through
    // unchanged. `undefined` clears the slot. Reading from `draft`
    // (not `client.legal`) so explicit clears via `setImageFile([])`
    // round-trip correctly.
    for (const field of IMAGE_FIELDS) {
      next[field] = draft[field];
    }

    // Honor the license toggle — when off, scrub everything license-
    // related from the patch payload so we don't leave orphaned ids /
    // expiries hanging on the doc.
    if (!draft.hasLicense) {
      next.licenseId = undefined;
      next.licenseImage = undefined;
      next.licenseType = undefined;
      next.licenseExpiryDate = undefined;
    }

    await updateClient.mutateAsync({ _id: client._id, legal: next });
  };

  // Existing-URL helpers for `FileUploader.existingUrls` — derived
  // memos so the consumer can pass a stable array reference and avoid
  // re-rendering the uploader on every keystroke. We prefer the
  // persisted `url` and fall back to a `data:` `upload` blob (so a
  // freshly-picked file still previews before it's persisted).
  const registrationExistingUrls = useMemo(
    () => pickPreviewUrls(draft.registrationImage),
    [draft.registrationImage],
  );
  const taxExistingUrls = useMemo(
    () => pickPreviewUrls(draft.taxImage),
    [draft.taxImage],
  );
  const licenseExistingUrls = useMemo(
    () => pickPreviewUrls(draft.licenseImage),
    [draft.licenseImage],
  );

  return {
    draft,
    setField,
    setImageFile,
    registrationFiles,
    taxFiles,
    licenseFiles,
    registrationExistingUrls,
    taxExistingUrls,
    licenseExistingUrls,
    reset,
    onSave,
    isPending: updateClient.isPending,
    isSuccess: updateClient.isSuccess,
  };
}

function pickPreviewUrls(image: ImageWithUpload | undefined): string[] {
  if (!image) return [];
  if (typeof image.url === 'string' && image.url) return [image.url];
  if (typeof image.upload === 'string' && image.upload) return [image.upload];
  return [];
}
